/**
 * Auth-aware fetch — cookie-only edition (CF rebuild).
 *
 * The CF Worker uses an opaque session cookie (__Host-pp_session in
 * prod, pp_session in dev). We don't carry tokens in localStorage,
 * so this module is much simpler than the original PP/arx version:
 * no JWT decode, no refresh, no Authorization header.
 *
 * It still owns:
 *   - 401 handling (dispatches via onAuthError + AuthExpiredError)
 *   - apiEventSource SSE wrapper with reconnect / visibility / probe
 *
 * The exported surface matches the original so the rest of the app
 * keeps working unchanged.
 */
import { onAuthError } from '../hooks/useAuth';

const API = '/api';

export class AuthExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'AuthExpiredError';
  }
}

/** Cookie-only auth — there is no token to surface. Kept for API
 * compatibility with the original PP code that called getToken(). */
export function getToken(): string | null {
  return null;
}

/** No-op in the cookie world; original kept for callers that await it. */
export async function ensureFreshToken(): Promise<void> {
  return;
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  // Don't set Authorization — cookie carries the session.
  const res = await fetch(input, {
    credentials: 'include',
    ...init,
    headers,
  });
  if (res.status === 401) {
    onAuthError();
    throw new AuthExpiredError();
  }
  return res;
}

/**
 * Auth-aware upload with real progress reporting.
 *
 * `fetch` cannot surface upload progress in any browser today (the
 * spec for `ReadableStream.from(request.body)` is implemented
 * inconsistently), so for file uploads we drop to XMLHttpRequest,
 * which exposes `xhr.upload.onprogress` with byte-accurate
 * `loaded` / `total` values.
 *
 * Mirrors `apiFetch` semantics where they overlap: cookies travel
 * via `withCredentials`, 401 surfaces through the AuthExpired path.
 * Returns a normal `Response` so callers keep their existing
 * `.json()` / `.ok` ergonomics.
 */
export async function apiUpload(
  url: string,
  formData: FormData | Blob,
  options: { onProgress?: (loaded: number, total: number) => void; signal?: AbortSignal } = {},
): Promise<Response> {
  return new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    // Note: do NOT set Content-Type — letting XHR auto-set the
    // multipart/form-data boundary is required for the server to parse.

    if (options.onProgress) {
      xhr.upload.onprogress = (e: ProgressEvent) => {
        if (e.lengthComputable) options.onProgress!(e.loaded, e.total);
      };
    }

    xhr.onload = () => {
      const body = xhr.response;
      const headers = new Headers();
      xhr.getAllResponseHeaders().split('\r\n').forEach(line => {
        const idx = line.indexOf(':');
        if (idx > 0) headers.set(line.slice(0, idx).trim(), line.slice(idx + 1).trim());
      });
      const res = new Response(body, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers,
      });
      if (xhr.status === 401) {
        onAuthError();
        reject(new AuthExpiredError());
        return;
      }
      resolve(res);
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    if (options.signal) {
      options.signal.addEventListener('abort', () => xhr.abort());
    }

    xhr.send(formData);
  });
}

export interface ApiEventSourceHandlers {
  onMessage?: (e: MessageEvent) => void;
  onOpen?: (e: Event) => void;
  onError?: (e: Event) => void;
  onAuthExpired?: () => void;
}

export interface ApiEventSourceHandle {
  close(): void;
  reconnect(delayMs?: number): void;
}

type ConnectCause = 'open' | 'retry' | 'visibility' | 'manual';

function withSeqAndCause(
  baseUrl: string,
  seq: number,
  cause: ConnectCause,
): string {
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}seq=${seq}&cause=${cause}`;
}

export function apiEventSource(
  url: string,
  handlers: ApiEventSourceHandlers = {},
): ApiEventSourceHandle {
  let es: EventSource | null = null;
  let retryDelay = 1000;
  let cancelled = false;
  let everOpened = false;
  let pendingRetry: ReturnType<typeof setTimeout> | null = null;
  let seq = 0;

  async function probeAuth(): Promise<boolean> {
    try {
      await apiFetch(`${API}/auth/me`);
      return true;
    } catch (err) {
      if (err instanceof AuthExpiredError) return false;
      return true;
    }
  }

  async function connect(cause: ConnectCause): Promise<void> {
    if (cancelled) return;
    pendingRetry = null;
    seq += 1;
    const taggedUrl = withSeqAndCause(url, seq, cause);
    es = new EventSource(taggedUrl, { withCredentials: true });

    es.onopen = (e) => {
      everOpened = true;
      retryDelay = 1000;
      handlers.onOpen?.(e);
    };

    if (handlers.onMessage) es.onmessage = handlers.onMessage;

    es.onerror = async (e) => {
      const hadBeenOpen = everOpened;
      es?.close();
      if (cancelled) return;

      if (hadBeenOpen) {
        const stillAuthed = await probeAuth();
        if (!stillAuthed) {
          cancelled = true;
          handlers.onAuthExpired?.();
          return;
        }
      }

      handlers.onError?.(e);
      pendingRetry = setTimeout(() => connect('retry'), retryDelay);
      retryDelay = Math.min(retryDelay * 2, 10000);
    };
  }

  function onVisibilityChange() {
    if (cancelled) return;
    if (
      typeof document === 'undefined' ||
      document.visibilityState !== 'visible'
    )
      return;
    if (es && es.readyState === EventSource.OPEN) return;
    if (pendingRetry) {
      clearTimeout(pendingRetry);
      pendingRetry = null;
    }
    retryDelay = 1000;
    es?.close();
    connect('visibility');
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }

  connect('open');

  return {
    close() {
      cancelled = true;
      if (pendingRetry) {
        clearTimeout(pendingRetry);
        pendingRetry = null;
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      }
      es?.close();
    },
    reconnect(delayMs = 500) {
      if (pendingRetry) {
        clearTimeout(pendingRetry);
        pendingRetry = null;
      }
      es?.close();
      retryDelay = 1000;
      pendingRetry = setTimeout(() => connect('manual'), delayMs);
    },
  };
}

/** PP login response shape — kept for API compatibility. */
export interface CookieLoginResult {
  token: string;
  user_id: string;
  username: string;
  role: string;
}

/** Synthesize PP's localStorage-backed user shape from the CF /me payload. */
export function userFromMe(me: {
  user: { id: string; email: string; display_name: string | null };
}): CookieLoginResult {
  return {
    token: 'cookie-auth',
    user_id: me.user.id,
    username: me.user.display_name || me.user.email,
    role: 'user',
  };
}
